import {
  TOGGLE_CHAT_AGENT,
  SEND_MESSAGE_REQUEST,
  SEND_MESSAGE_SUCCESS,
  SEND_MESSAGE_FAILURE,
  SEND_MESSAGE_CANCEL,
  SET_CHAT_CONTEXT,
  MESSAGE_TYPES,
  ADD_USER_MESSAGE,
  // Conversation management
  CREATE_CONVERSATION_REQUEST,
  CREATE_CONVERSATION_SUCCESS,
  CREATE_CONVERSATION_FAILURE,
  LOAD_CONVERSATIONS_REQUEST,
  LOAD_CONVERSATIONS_SUCCESS,
  LOAD_CONVERSATIONS_FAILURE,
  LOAD_CONVERSATION_DETAILS_REQUEST,
  LOAD_CONVERSATION_DETAILS_SUCCESS,
  LOAD_CONVERSATION_DETAILS_FAILURE,
  DELETE_CONVERSATION_REQUEST,
  DELETE_CONVERSATION_SUCCESS,
  DELETE_CONVERSATION_FAILURE,
  UPDATE_CONVERSATION_TITLE_REQUEST,
  UPDATE_CONVERSATION_TITLE_SUCCESS,
  UPDATE_CONVERSATION_TITLE_FAILURE,
  SET_ACTIVE_CONVERSATION,
  SET_LAST_EMPTY_CONVERSATION,
  // Progress tracking
  PROGRESS_UPDATE,
  AGENT_MESSAGE_UPDATE,
  PIPELINE_START,
  PIPELINE_COMPLETE,
  PIPELINE_ERROR,
  RESET_PROGRESS,
  TITLE_UPDATED,
  THINKING_STREAM,
  // Category disambiguation
  CATEGORY_SELECTION_SUBMITTED,
  STORE_CATEGORY_SELECTION_REQUEST,
  STORE_CATEGORY_SELECTION_SUCCESS,
  STORE_CATEGORY_SELECTION_FAILURE,
  // Message feedback
  UPDATE_MESSAGE_FEEDBACK,
  // Message regeneration
  REGENERATE_MESSAGE_REQUEST,
  REGENERATE_MESSAGE_SUCCESS,
  REGENERATE_MESSAGE_FAILURE,
  REMOVE_AGENT_MESSAGE,
  // Screen and filter management
  SET_CURRENT_SCREEN,
  FETCH_CHAT_FILTERS_REQUEST,
  FETCH_CHAT_FILTERS_SUCCESS,
  FETCH_CHAT_FILTERS_FAILURE,
} from "./constants";

import { API_URL_V4 } from "../../config";

import ChatAgentService from "../../services/chatAgentService";

const chatAgentService = new ChatAgentService();

// ========================================
// SCREEN AND FILTER MANAGEMENT
// ========================================

// Map screen names to viewType IDs
const SCREEN_TO_VIEW_TYPE = {
  brandPerformance: 11,
  marketShare: 6,
};

export const setCurrentScreen = (screen) => ({
  type: SET_CURRENT_SCREEN,
  payload: { screen },
});

// Fetch filter definitions using existing insights API
// viewType: 11 = Brand Performance, 6 = Market Share
// dateRange: { startDate: 'YYYY/MM/DD', endDate: 'YYYY/MM/DD' }
export const fetchChatFilters = (viewType = 11, dateRange = null) => async (dispatch) => {
  try {
    dispatch({ type: FETCH_CHAT_FILTERS_REQUEST });
    
    const result = await chatAgentService.getFilterDefinitions(viewType, dateRange);
    
    dispatch({
      type: FETCH_CHAT_FILTERS_SUCCESS,
      payload: {
        filterDefinitions: result.filterDefinitions || [],
      }
    });
    
    return result;
  } catch (error) {
    console.error("Failed to fetch chat filters:", error);
    
    dispatch({
      type: FETCH_CHAT_FILTERS_FAILURE,
      payload: { error: error.message || "Failed to load filters" }
    });
  }
};

// Fetch dynamic filters (brands, keywords) for a specific retailer
// Uses existing API: /insights/views/dynamicFilterDefinitions?retailer=xxx
// dateRange headers: { startDate: 'YYYY/MM/DD', endDate: 'YYYY/MM/DD' }
export const fetchDynamicChatFilters = (retailer, categories = null, dateRange = null) => async (dispatch, getState) => {
  if (!retailer) return;
  
  try {
    dispatch({ type: FETCH_CHAT_FILTERS_REQUEST });
    
    const result = await chatAgentService.getDynamicFilterValues(retailer, categories, dateRange);
    
    // Return the dynamic brands/keywords - these need to be merged into filterDefinitions by the component
    dispatch({
      type: FETCH_CHAT_FILTERS_SUCCESS,
      payload: {
        dynamicBrands: result.brands || [],
        dynamicKeywords: result.keywords || [],
      }
    });
    
    return result;
  } catch (error) {
    console.error("Failed to fetch dynamic chat filters:", error);
    
    dispatch({
      type: FETCH_CHAT_FILTERS_FAILURE,
      payload: { error: error.message || "Failed to load dynamic filters" }
    });
  }
};

// Helper to get viewType from current screen
export const getViewTypeForScreen = (screen) => {
  return SCREEN_TO_VIEW_TYPE[screen] || 11; // Default to Brand Performance
};

// ========================================
// BASIC CHAT ACTIONS
// ========================================

export const toggleChatAgent = () => ({
  type: TOGGLE_CHAT_AGENT,
});

export const setChatContext = (context) => ({
  type: SET_CHAT_CONTEXT,
  payload: { context },
});

export const addUserMessage = (conversationId, message) => ({
  type: ADD_USER_MESSAGE,
  payload: { conversationId, message },
});

export const messageRequested = () => ({
  type: SEND_MESSAGE_REQUEST,
});

export const messageSuccess = (response, conversationId) => ({
  type: SEND_MESSAGE_SUCCESS,
  payload: { response, conversationId },
});

export const setLastEmptyConversation = (conversationId) => ({
  type: SET_LAST_EMPTY_CONVERSATION,
  payload: { conversationId }
});

export const messageError = (error) => ({
  type: SEND_MESSAGE_FAILURE,
  payload: { error },
});

export const messageCancel = () => ({
  type: SEND_MESSAGE_CANCEL,
});

// ========================================
// PROGRESS TRACKING
// ========================================

export const progressUpdate = (data) => ({
  type: PROGRESS_UPDATE,
  payload: data
});

export const agentMessageUpdate = (data) => ({
  type: AGENT_MESSAGE_UPDATE,
  payload: data
});

export const pipelineStart = (data) => ({
  type: PIPELINE_START,
  payload: data
});

export const pipelineComplete = (data) => ({
  type: PIPELINE_COMPLETE,
  payload: data
});

export const pipelineError = (error) => ({
  type: PIPELINE_ERROR,
  payload: { error }
});

export const resetProgress = (conversationId = null) => ({
  type: RESET_PROGRESS,
  payload: { conversationId }
});

export const titleUpdated = (data) => ({
  type: TITLE_UPDATED,
  payload: data
});

// ========================================
// WEBSOCKET MANAGEMENT
// ========================================

export const connectProgressWebSocket = (conversationId) => (dispatch) => {
  const handleProgress = (message) => {
    switch (message.type) {
      case 'pipeline_start':
        dispatch(pipelineStart(message.data || message));
        break;
      case 'progress':
        dispatch(progressUpdate(message.data));
        break;
      case 'agent_message':
        dispatch(agentMessageUpdate(message));
        break;
      case 'pipeline_complete':
      case 'complete':
        dispatch(pipelineComplete(message.data || message));
        break;
      case 'pipeline_error':
      case 'error':
        dispatch(pipelineError(message.data?.error || message.error || 'Unknown error'));
        break;
      case 'title_updated':
        dispatch(titleUpdated(message.data));
        break;
      case 'thinking_stream':
        dispatch(thinkingStreamUpdate(message.stage, message.text, message.agent, message.conversationId));
        break;
      default:
        // Stage/progress objects sent without a type field (bare progress updates)
        if (message.stage && message.status) {
          dispatch(progressUpdate(message));
        } else {
          console.log('Unknown WebSocket message type:', message.type);
        }
    }
  };
  
  const handleError = (error) => {
    dispatch(pipelineError('WebSocket connection error'));
  };
  
  chatAgentService.connectProgressWebSocket(conversationId, handleProgress, handleError);
};

export const thinkingStreamUpdate = (stage, text, agent, conversationId = null) => ({
  type: THINKING_STREAM,
  payload: { stage, text, agent, conversationId },
});

export const disconnectProgressWebSocket = (conversationId = null) => (dispatch) => {
  chatAgentService.disconnectProgressWebSocket(conversationId);
  dispatch(resetProgress(conversationId));
};

// ========================================
// CONVERSATION MANAGEMENT
// ========================================

export const setActiveConversation = (conversationId) => ({
  type: SET_ACTIVE_CONVERSATION,
  payload: { conversationId }
});

export const createConversation = (initialMessage = "") => async (dispatch) => {
  try {
    dispatch({ type: CREATE_CONVERSATION_REQUEST });
    
    const response = await chatAgentService.createConversation(initialMessage);
    
    dispatch({
      type: CREATE_CONVERSATION_SUCCESS,
      payload: { 
        conversationId: response.conversationId,
        createdAt: response.createdAt
      }
    });
    
    dispatch(setActiveConversation(response.conversationId));
    dispatch(connectProgressWebSocket(response.conversationId));

    return response.conversationId;
    
  } catch (error) {
    console.error("Failed to create conversation:", error);
    
    // Generate fallback conversation ID
    const fallbackConversationId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    dispatch({
      type: CREATE_CONVERSATION_SUCCESS,
      payload: { 
        conversationId: fallbackConversationId,
        createdAt: new Date().toISOString(),
        fallback: true
      }
    });
    
    dispatch(setActiveConversation(fallbackConversationId));
    dispatch(connectProgressWebSocket(fallbackConversationId));
    
    return fallbackConversationId;
  }
};

export const loadUserConversations = (limit = 50) => async (dispatch) => {
  try {
    dispatch({ type: LOAD_CONVERSATIONS_REQUEST });
    
    const result = await chatAgentService.getUserConversations(limit);
    
    dispatch({
      type: LOAD_CONVERSATIONS_SUCCESS,
      payload: { 
        conversations: result.conversations,
        total: result.total
      }
    });
    
  } catch (error) {
    console.error("Failed to load conversations:", error);
    
    dispatch({
      type: LOAD_CONVERSATIONS_FAILURE,
      payload: { error: error.message }
    });
  }
};

export const switchConversation = (conversationId) => async (dispatch) => {
  try {
    dispatch({ type: LOAD_CONVERSATION_DETAILS_REQUEST });
    
    const conversation = await chatAgentService.getConversationDetails(conversationId);
    
    // Normalize historical messages
    const normalizedMessages = conversation.messages ? conversation.messages.map(msg => ({
      ...msg,
      sender: msg.type === 'USER' ? 'user' : 'agent',
      text: msg.content || msg.text,
    })) : [];
    
    const normalizedConversation = {
      ...conversation,
      messages: normalizedMessages
    };
    
    dispatch({
      type: LOAD_CONVERSATION_DETAILS_SUCCESS,
      payload: { 
        conversationId,
        conversation: normalizedConversation
      }
    });
    
    dispatch(setActiveConversation(conversationId));
    dispatch(connectProgressWebSocket(conversationId));
   
  } catch (error) {
    console.error("Failed to switch conversation:", error);
    
    dispatch({
      type: LOAD_CONVERSATION_DETAILS_FAILURE,
      payload: { error: error.message }
    });
  }
};

export const deleteConversation = (conversationId) => async (dispatch) => {
  try {
    dispatch({ type: DELETE_CONVERSATION_REQUEST });
    dispatch(disconnectProgressWebSocket(conversationId));
    
    await chatAgentService.deleteConversation(conversationId);
    
    dispatch({
      type: DELETE_CONVERSATION_SUCCESS,
      payload: { conversationId }
    });
    
    dispatch(loadUserConversations());
    
  } catch (error) {
    console.error("Failed to delete conversation:", error);
    
    dispatch({
      type: DELETE_CONVERSATION_FAILURE,
      payload: { error: error.message }
    });
  }
};

export const updateConversationTitle = (conversationId, title) => async (dispatch) => {
  try {
    dispatch({ type: UPDATE_CONVERSATION_TITLE_REQUEST });
    
    await chatAgentService.updateConversationTitle(conversationId, title);
    
    dispatch({
      type: UPDATE_CONVERSATION_TITLE_SUCCESS,
      payload: { conversationId, title }
    });
    
  } catch (error) {
    console.error("Failed to update conversation title:", error);
    
    dispatch({
      type: UPDATE_CONVERSATION_TITLE_FAILURE,
      payload: { error: error.message }
    });
  }
};

// ========================================
// SEND MESSAGE - SIMPLIFIED WITH MINIMAL CONTEXT
// ========================================

export const sendMessage = (message, categorySelection = null) => async (dispatch, getState) => {
  const state = getState();
  const { activeConversationId, currentScreen } = state.chatAgent || {};

  const executeMessageSend = async (conversationId, isNewConversation = false) => {
    const userMessage = {
      id: Date.now(),
      type: MESSAGE_TYPES.USER,
      sender: 'user',
      text: message,
      content: message,
      timestamp: new Date().toISOString(),
      conversationId: conversationId
    };

    dispatch(addUserMessage(conversationId, userMessage));
    dispatch(messageRequested());
    
    if (!isNewConversation) {
      dispatch(resetProgress(conversationId));
    }

    dispatch(pipelineStart({ conversationId }));

    // Minimal context - only screen identifier
    // Backend handles all data lookup based on user session
    const minimalContext = {
      screen: currentScreen,
    };

    try {
      const response = await chatAgentService.sendMessage(message, minimalContext, conversationId, categorySelection);
      
      const messageObj = {
        id: Date.now() + 1,
        type: MESSAGE_TYPES.AGENT,
        sender: 'agent',
        text: response.response,
        content: response.response,
        timestamp: new Date().toISOString(),
        conversationId: conversationId,
        tables: response.tables,
        charts: response.charts,
        data: response.data,
        metadata: response.metadata,
        citations: response.citations || []
      };
      
      dispatch(messageSuccess(messageObj, conversationId));

      // Persist category picker to backend so it survives page reload
      if (response?.metadata?.type === 'category_selection') {
        dispatch(storeCategorySelectionMessage(conversationId, {
          id: messageObj.id,
          type: 'CATEGORY_SELECTION',
          sender: 'agent',
          timestamp: messageObj.timestamp,
          conversationId: conversationId,
          selectionPayload: response.metadata.selection_payload,
          submitted: false,
          selectedValues: [],
        }));
      }
      
      // Force pipeline completion when HTTP response arrives
      const currentState = getState();
      const activePipeline = currentState.chatAgent?.conversations?.[conversationId]?.pipeline;
      const isPipelineStillActive = activePipeline?.pipelineActive || currentState.chatAgent?.pipelineActive;
      
      if (isPipelineStillActive) {
        dispatch(pipelineComplete({
          conversationId,
          execution_path: 'http_fallback',
          forced: true
        }));
      }
      
    } catch (error) {
      dispatch(resetProgress(conversationId));
      if (error.message === "cancel") {
        dispatch(messageCancel());
      } else {
        dispatch(messageError(error.message));
      }
    }
  };

  // Create conversation if none exists
  if (!activeConversationId) {
    try {
      const newConversationId = await dispatch(createConversation(message));
      await executeMessageSend(newConversationId, true);
    } catch (error) {
      dispatch(messageError("Failed to create conversation"));
    }
  } else {
    await executeMessageSend(activeConversationId, false);
  }
};

// ========================================
// CATEGORY SELECTION
// ========================================

export const storeCategorySelectionRequest = () => ({
  type: STORE_CATEGORY_SELECTION_REQUEST
});

export const storeCategorySelectionSuccess = (messageId) => ({
  type: STORE_CATEGORY_SELECTION_SUCCESS,
  payload: { messageId }
});

export const storeCategorySelectionFailure = (error) => ({
  type: STORE_CATEGORY_SELECTION_FAILURE,
  payload: { error }
});

export const storeCategorySelectionMessage = (conversationId, message) =>
  async (dispatch, getState) => {
    dispatch(storeCategorySelectionRequest());

    try {
      const token = chatAgentService.getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL_V4}/chat-agent/conversations/${conversationId}/category-selection`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          selection_payload: message.selectionPayload,
          id: message.id,
          submitted: message.submitted,
          selected_values: message.selectedValues,
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to store category selection: ${response.status}`);
      }

      const result = await response.json();
      dispatch(storeCategorySelectionSuccess(result.message_id));

    } catch (error) {
      console.error('Failed to store category selection message:', error);
      dispatch(storeCategorySelectionFailure(error.message));
    }
  };

export const submitCategorySelection = (selectedValues, selectionMessage) =>
  async (dispatch, getState) => {

    // Mark the picker as submitted in state (freezes it)
    dispatch({
      type: CATEGORY_SELECTION_SUBMITTED,
      payload: { messageId: selectionMessage.id, selectedValues },
    });

    // Persist submitted state to backend so picker shows frozen on page reload
    const conversationId = getState().chatAgent?.activeConversationId;
    if (conversationId) {
      dispatch(storeCategorySelectionMessage(conversationId, {
        id: selectionMessage.id,
        type: 'CATEGORY_SELECTION',
        sender: 'agent',
        timestamp: selectionMessage.timestamp,
        conversationId: conversationId,
        selectionPayload: selectionMessage.selectionPayload,
        submitted: true,
        selectedValues: selectedValues,
      }));
    }
    // Build descriptive echo message from the resolved selection
    const displayNames = selectedValues.map(v => {
      const opt = (selectionMessage.selectionPayload?.options || []).find(o => o.value === v);
      return opt?.display_name || v.split('>').pop().trim();
    });

    const orig = selectionMessage.selectionPayload?.original_request || {};
    const contextParts = [];
    if (orig.retailer) contextParts.push(`on ${orig.retailer}`);
    if (orig.start_date || orig.end_date) {
      const from = orig.start_date || '';
      const to = orig.end_date || '';
      contextParts.push(`from ${from}${to ? ` to ${to}` : ''}`.trim());
    }
    const contextSuffix = contextParts.length > 0 ? ` ${contextParts.join(' ')}` : '';

    const echoMessage = `Show top brands for ${displayNames.join(' and ')}${contextSuffix}`;

    dispatch(sendMessage(echoMessage, selectedValues));
  };

// ========================================
// MESSAGE FEEDBACK
// ========================================

export const updateMessageFeedback = (messageId, feedbackType, feedbackText = null) => ({
  type: UPDATE_MESSAGE_FEEDBACK,
  payload: {
    messageId,
    feedbackType,
    feedbackText,
    timestamp: new Date().toISOString(),
  },
});

// ========================================
// MESSAGE REGENERATION
// ========================================

export const removeAgentMessage = (messageId) => ({
  type: REMOVE_AGENT_MESSAGE,
  payload: { messageId }
});

export const regenerateMessageRequest = () => ({
  type: REGENERATE_MESSAGE_REQUEST
});

export const regenerateMessageSuccess = (response, conversationId) => ({
  type: REGENERATE_MESSAGE_SUCCESS,
  payload: { response, conversationId }
});

export const regenerateMessageFailure = (error) => ({
  type: REGENERATE_MESSAGE_FAILURE,
  payload: { error }
});

export const regenerateMessage = (messageId) => async (dispatch, getState) => {
  const state = getState();
  const { activeConversationId } = state.chatAgent || {};

  if (!activeConversationId) {
    console.error('No active conversation to regenerate message in');
    return;
  }

  try {
    // 1. Remove the agent message from UI
    dispatch(removeAgentMessage(messageId));
    
    // 2. Reset progress and start loading state (same as sendMessage)
    dispatch(resetProgress(activeConversationId));
    dispatch(regenerateMessageRequest());
    dispatch(pipelineStart({ conversationId: activeConversationId }));
    
    // 3. Call the regenerate endpoint (this will trigger WebSocket progress updates)
    const response = await chatAgentService.regenerateMessage(messageId, activeConversationId);
    
    // 4. Create new agent message from response (same format as sendMessage)
    // IMPORTANT: Use the original_message_id from metadata if available, otherwise use the messageId we sent
    const newMessageId = response.metadata?.original_message_id || messageId;
    
    const agentMessage = {
      id: newMessageId, // Use original message ID to maintain consistency
      type: MESSAGE_TYPES.AGENT,
      sender: 'agent',
      text: response.response,
      content: response.response,
      timestamp: new Date().toISOString(),
      conversationId: activeConversationId,
      charts: response.charts || [],
      tables: response.tables || [],
      data: response.data,
      citations: response.citations || [],
      metadata: {
        ...response.metadata,
        regenerated: true,
        original_message_id: newMessageId, // Store original ID for future regenerations
      },
    };
    
    // 5. Dispatch success with new message
    dispatch(regenerateMessageSuccess(agentMessage, activeConversationId));
    
    // 6. Force pipeline completion if still active (same as sendMessage)
    const currentState = getState();
    const activePipeline = currentState.chatAgent?.conversations?.[activeConversationId]?.pipeline;
    const isPipelineStillActive = activePipeline?.pipelineActive || currentState.chatAgent?.pipelineActive;
    
    if (isPipelineStillActive) {
      dispatch(pipelineComplete({
        conversationId: activeConversationId,
        execution_path: 'http_fallback',
        forced: true
      }));
    }
    
  } catch (error) {
    console.error('❌ Failed to regenerate message:', error);
    dispatch(resetProgress(activeConversationId));
    dispatch(regenerateMessageFailure(error.message || 'Failed to regenerate message'));
    dispatch(pipelineError(error.message || 'Failed to regenerate message'));
  }
};
